/* eslint-disable indent */
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {MainLoader, DataGrid, ConfirmationModalPromise} from '@components';
import {GreenButton, StyledH3, WhiteButton} from '@styled';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useHistory, useParams} from 'react-router-dom';
import styled from 'styled-components';
import {Breadcrumb} from 'react-bootstrap';
import {sagaActions} from './redux/saga';
import AddEditCategorySource from './AddEditCategorySource';

const StyledHeadRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  .heading {
  }
  .actions {
  }
`;

const RowActions = ({data, onAction}) => {
  return (
    <div>
      <WhiteButton
        onClick={() => onAction('edit', data)}
        style={{margin: '.16em'}}
      >
        <i className="fa fa-edit mr-1" />
        Edit
      </WhiteButton>
      <WhiteButton
        onClick={() => onAction('delete', data)}
        style={{margin: '.16em'}}
      >
        <i className="fa fa-trash mr-1" />
        Delete
      </WhiteButton>
      <WhiteButton
        onClick={() => onAction('status', data)}
        style={{margin: '.16em'}}
      >
        {/* <i className="fa fa-edit mr-1" /> */}
        Make {data.status === 'Active' ? 'InActive' : 'Active'}
      </WhiteButton>
      <WhiteButton
        onClick={() => onAction('equations', data)}
        style={{margin: '.16em'}}
      >
        {/* <i className="fa fa-tools mr-1" /> */}
        Equations
      </WhiteButton>
      <WhiteButton
        onClick={() => onAction('input-tables', data)}
        style={{margin: '.16em'}}
      >
        {/* <i className="fa fa-tools mr-1" /> */}
        Input Tables
      </WhiteButton>
    </div>
  );
};

const ManageCategorySources = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const remoteData = useSelector((state) => state.calcEngine.categorySources);
  const isLoading = useSelector(
    (state) => state.calcEngine.categorySources.loading
  );
  const [editRow, setEditRow] = useState(null);
  const [showAddEditModal, setShowAddEditModal] = useState(false);

  const history = useHistory();
  const {categoryId} = useParams();

  const [pagination, setPagination] = React.useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    sortOn: 'name',
    sortDir: 'DESC',
  });

  useEffect(() => {
    if (
      remoteData.page !== pagination.page ||
      remoteData.pageSize !== pagination.pageSize ||
      remoteData.totalCount !== pagination.totalCount ||
      remoteData.sortOn !== pagination.sortOn ||
      remoteData.sortDir !== pagination.sortDir
    ) {
      setPagination({
        page: remoteData.page,
        pageSize: remoteData.pageSize,
        totalCount: remoteData.totalCount,
        sortOn: remoteData.sortOn,
        sortDir: remoteData.sortDir,
      });
    }
  }, [remoteData]);

  const fetchData = ({
    page = 1,
    pageSize = pagination.pageSize,
    sortOn,
    sortDir,
  }) => {
    dispatch({
      type: sagaActions.FETCH_CATEGORY_SOURCES,
      payload: {
        categoryId,
        values: {
          page,
          pageSize,
          sortOn,
          sortDir,
        },
      },
    });
  };

  const handleDeleteSource = (remove) => {
    dispatch({
      type: sagaActions.REMOVE_CATEGORY_SOURCE,
      payload: {
        categoryId,
        sourceId: remove.id,
        values: {},
      },
    });
  };

  const handleUpdateSourceStatus = (row) => {
    const updateStatusTo = row.status === 'Active' ? 'Inactive' : 'Active';
    dispatch({
      type: sagaActions.UPDATE_CATEGORY_SOURCE,
      payload: {categoryId, sourceId: row.id, values: {status: updateStatusTo}},
    });
  };

  const handleRowAction = useCallback(
    (action, row) => {
      switch (action) {
        case 'edit': {
          setEditRow(row);
          setShowAddEditModal(true);
          break;
        }
        case 'equations':
          history.push(
            `/calc-engine/${categoryId}/sources/${row.id}/equations`
          );
          break;
        case 'delete': {
          ConfirmationModalPromise({
            title: `Are you sure you want to delete this [${row.name}] category source?`,
            show: true,
            confirmBtn: 'Yes',
            cancelBtn: 'No',
            headTitle: 'Confirmation!',
          }).then(
            () => {
              handleDeleteSource(row);
            },
            () => {
              //  do nothing on failure
            }
          );
          break;
        }
        case 'status': {
          ConfirmationModalPromise({
            title: `Are you sure you want to make source [${
              row.status === 'Active' ? 'inactive' : 'active'
            }]?`,
            show: true,
            confirmBtn: 'Yes',
            cancelBtn: 'No',
            headTitle: 'Confirmation!',
          }).then(
            () => {
              handleUpdateSourceStatus(row);
            },
            () => {
              //  do nothing on failure
            }
          );
          break;
        }
        case 'input-tables':
          history.push(
            `/calc-engine/${categoryId}/sources/${row.id}/input-table-fields`
          );
          break;
        default:
          break;
      }
    },
    [editRow, showAddEditModal]
  );

  const handleAddCategory = () => {
    setShowAddEditModal(true);
  };

  const handleToggleAddEditModal = useCallback(() => {
    setEditRow(null);
    setShowAddEditModal(false);
  }, [editRow, showAddEditModal]);

  const renderAddEditModal = () => {
    return (
      <AddEditCategorySource
        show={showAddEditModal}
        categoryId={categoryId}
        editRow={editRow}
        toggleModal={handleToggleAddEditModal}
      />
    );
  };

  const renderRowActions = (colProps) => {
    return <RowActions data={colProps} onAction={handleRowAction} />;
  };

  const Columns = React.useMemo(
    () => [
      {
        accessor: 'id',
        Header: '#',
        className: 'grey-column d-none d-lg-block',
        width: 50,
        tooltip: false,
        hidden: true,
      },
      {
        accessor: 'name',
        Header: 'Emission Source',
        tooltip: false,
        className: 'd-none d-lg-block',
      },
      {
        accessor: 'status',
        Header: 'Status',
        tooltip: false,
        className: 'd-none d-lg-block',
      },
      {
        id: 'actions',
        Header: <div className="d-none d-lg-block">Actions</div>,
        accessor: renderRowActions,
        disableSortBy: true,
        disableFilters: true,
        width: 150,
      },
    ],
    [pagination]
  );

  return (
    <>
      {isLoading && (
        <MainLoader
          minHeight="100vh"
          spinnerColor="#479541"
          background="rgba(0,0,0,0.2)"
        />
      )}
      <div>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{to: '/'}}>
            Home
          </Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} linkProps={{to: '/calc-engine'}}>
            Source Categories
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Emission Sources</Breadcrumb.Item>
        </Breadcrumb>
        <StyledH3>{t('owner.calcEngine.pageHeading')}</StyledH3>
        <StyledHeadRow>
          <div className="heading">
            {t('owner.calcEngine.categorySources.pageHeading')}{' '}
            {remoteData.category ? ` of ${remoteData.category.name}` : ''}
          </div>
          <div className="actions">
            <GreenButton className="ml-1" onClick={handleAddCategory}>
              <i className="fa fa-plus mr-1" />
              {t('owner.calcEngine.categorySources.addSource')}
            </GreenButton>
          </div>
        </StyledHeadRow>
        <DataGrid
          columns={Columns}
          data={remoteData.data}
          onFetchData={fetchData}
          pagination={pagination}
          filtering={false}
          expandEnabled={false}
        />
      </div>
      {renderAddEditModal()}
    </>
  );
};

export default ManageCategorySources;
